import { Dispatch } from '@reduxjs/toolkit';
import { saveAs } from 'file-saver';
import { keyBy } from 'lodash/fp';

import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { Account, FieldsMapLegacyUser } from 'dashboard/models/account';
import { Case, CaseObject } from 'dashboard/models/case';
import { Environment } from 'dashboard/models/environment';
import { Report, ReportMeta } from 'dashboard/models/report';
import { ReportAddressLookupAttributes } from 'dashboard/models/report/address-lookup';
import { ReportAdverseMediaAttributes } from 'dashboard/models/report/adverse-media';
import { ReportEmailAddressAttributes } from 'dashboard/models/report/email-address';
import { ReportSentilinkScoresAttributes } from 'dashboard/models/report/integration/sentilink-scores';
import { ReportPhoneNumberAttributes } from 'dashboard/models/report/phone-number';
import { ReportPoliticallyExposedPersonAttributes } from 'dashboard/models/report/politically-exposed-person';
import { ReportProfileAttributes } from 'dashboard/models/report/profile';
import { ReportProfileNonAuthoritativeAttributes } from 'dashboard/models/report/profile-non-authoritative';
import { ReportSocialMediaAttributes } from 'dashboard/models/report/social-media';
import { ReportSyntheticAttributes } from 'dashboard/models/report/synthetic';
import { ReportWatchlistAttributes } from 'dashboard/models/report/watchlist';
import { ReportRun } from 'dashboard/models/report-run';
import { ReportTemplate, ReportTemplateFilterOption } from 'dashboard/models/report-template';
import { Tag } from 'dashboard/models/tag';
import { setInquiryReports } from 'dashboard/redux/actions/inquiry';
import { setReport, setReportDetails } from 'dashboard/redux/actions/report';
import { setReportRuns } from 'dashboard/redux/actions/report-runs';
import { setAccountReports } from 'dashboard/redux/slices/account';
import { setCaseObjects } from 'dashboard/redux/slices/case';
import { DashboardThunkAction } from 'dashboard/redux/types';
import { FieldString } from 'lib/fields';
import { ApiEndpoint, ApiModel } from 'lib/interfaces/api';
import { Iso8601DateString } from 'lib/types';
import { assertNever } from 'lib/utils/types';

export const loadReport = (id: string, environment: Environment) => {
  return async (dispatch: Dispatch) => {
    const reportResponse = await SessionClientDashboard.getResource<Report, ReportMeta>(
      ApiEndpoint.Report,
      id,
    );
    const report = reportResponse.data;

    const relationshipMap = keyBy('id', reportResponse.included);

    const reportTemplate = relationshipMap[
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      report.relationships['report-template']?.data?.id
    ] as ReportTemplate;
    const tags = report.relationships.tags?.data.map(({ id }) => relationshipMap[id]) as Tag[];
    const cases = report.relationships.cases?.data.map(({ id }) => relationshipMap[id]) as Case[];
    const relatedReports = reportResponse.meta?.['related-reports'] ?? [];
    const reportRuns = reportResponse.included?.filter(
      (item) => item.type === ApiModel.ReportRunCustomList,
    ) as ReportRun[];

    // Dispatch details before report so we can safely use presence of report
    // to indicate that all data are fetched
    dispatch(
      setReportDetails({
        reportTemplate,
        tags,
        cases,
        relatedReports,
        reportRuns,
      }),
    );
    dispatch(setReportRuns(reportRuns, environment));
    dispatch(setReport(report));
  };
};

export const printReport = (id: string) => {
  return async () => {
    const blob = await SessionClientDashboard.getBlob(ApiEndpoint.Report, id, 'print');
    saveAs(blob, `${id}.pdf`);
  };
};

export const createReport = (
  reportTemplate: ReportTemplate | ReportTemplateFilterOption,
  account: Account,
  environment: Environment,
) => {
  return async () => {
    const legacyFields = account.attributes.fields as unknown as FieldsMapLegacyUser;
    const idNumbers = legacyFields.identification_numbers;
    const ssnField = idNumbers.value.find((p) => p.value.identification_class.value === 'ssn');

    const fieldName = account.attributes.fields['name']?.value as Record<string, FieldString>;
    const fieldAddress = account.attributes.fields['address']?.value as Record<string, FieldString>;

    const nameFirst = fieldName?.first?.value;
    const nameMiddle = fieldName?.middle?.value;
    const nameLast = fieldName?.last?.value;
    const addressStreet1 = fieldAddress?.street_1?.value;
    const addressStreet2 = fieldAddress?.street_2?.value;
    const addressCity = fieldAddress?.city?.value;
    const addressSubdivision = fieldAddress?.subdivision?.value;
    const addressPostalCode = fieldAddress?.postal_code?.value;
    const addressCountryCode = fieldAddress?.country_code?.value;
    const birthdate = account.attributes.fields.birthdate?.value as Iso8601DateString;
    const phoneNumber = account.attributes.fields.phone_number?.value as string;
    const emailAddress = account.attributes.fields.email_address?.value as string;

    switch (reportTemplate.type) {
      case ApiModel.ReportTemplateAddressLookup:
        await SessionClientDashboard.postResourceCreate<Report>({
          type: ApiModel.ReportAddressLookup,
          attributes: {
            'account-id': account.id,
            'environment-id': environment.id,
            'report-template-id': reportTemplate.id,
            'address-street-1': addressStreet1,
            'address-street-2': addressStreet2,
            'address-city': addressCity,
            'address-subdivision': addressSubdivision,
            'address-postal-code': addressPostalCode,
          } as ReportAddressLookupAttributes,
        });
        break;
      case ApiModel.ReportTemplateAdverseMedia:
        await SessionClientDashboard.postResourceCreate<Report>({
          type: ApiModel.ReportAdverseMedia,
          attributes: {
            'account-id': account.id,
            'environment-id': environment.id,
            'report-template-id': reportTemplate.id,
            'name-first': nameFirst,
            'name-middle': nameMiddle,
            'name-last': nameLast,
            birthdate: birthdate,
            'country-code': addressCountryCode,
          } as ReportAdverseMediaAttributes,
        });
        break;
      case ApiModel.ReportTemplateEmailAddress:
        await SessionClientDashboard.postResourceCreate<Report>({
          type: ApiModel.ReportEmailAddress,
          attributes: {
            'account-id': account.id,
            'environment-id': environment.id,
            'report-template-id': reportTemplate.id,
            'email-address': emailAddress,
          } as ReportEmailAddressAttributes,
        });
        break;
      case ApiModel.ReportTemplatePhoneNumber:
        await SessionClientDashboard.postResourceCreate<Report>({
          type: ApiModel.ReportPhoneNumber,
          attributes: {
            'account-id': account.id,
            'environment-id': environment.id,
            'report-template-id': reportTemplate.id,
            'phone-number': phoneNumber,
          } as ReportPhoneNumberAttributes,
        });
        break;
      case ApiModel.ReportTemplatePoliticallyExposedPerson:
        await SessionClientDashboard.postResourceCreate<Report>({
          type: ApiModel.ReportPoliticallyExposedPerson,
          attributes: {
            'account-id': account.id,
            'environment-id': environment.id,
            'report-template-id': reportTemplate.id,
            query: {
              'name-first': nameFirst,
              'name-middle': nameMiddle,
              'name-last': nameLast,
              birthdate: birthdate,
              'country-code': addressCountryCode,
            },
          } as ReportPoliticallyExposedPersonAttributes,
        });
        break;
      case ApiModel.ReportTemplateProfile: {
        await SessionClientDashboard.postResourceCreate<Report>({
          type: ApiModel.ReportProfile,
          attributes: {
            'account-id': account.id,
            'environment-id': environment.id,
            'report-template-id': reportTemplate.id,
            'name-first': nameFirst,
            'name-last': nameLast,
            birthdate: birthdate,
            'social-security-number': ssnField?.value?.identification_number?.value,
            'country-code': 'US',
            'address-street-1': addressStreet1,
            'address-street-2': addressStreet2,
            'address-city': addressCity,
            'address-subdivision': addressSubdivision,
            'address-postal-code': addressPostalCode,
          } as ReportProfileAttributes,
        });
        break;
      }
      case ApiModel.ReportTemplateProfileNonAuthoritative:
        await SessionClientDashboard.postResourceCreate<Report>({
          type: ApiModel.ReportProfileNonAuthoritative,
          attributes: {
            'account-id': account.id,
            'environment-id': environment.id,
            'report-template-id': reportTemplate.id,
            query: {
              'phone-number': phoneNumber,
              'email-address': emailAddress,
              'name-first': nameFirst,
              'name-middle': nameMiddle,
              'name-last': nameLast,
              'address-city': addressCity,
              'address-subdivision': addressSubdivision,
              'address-postal-code': addressPostalCode,
            },
          } as ReportProfileNonAuthoritativeAttributes,
        });
        break;
      case ApiModel.ReportTemplateSecActionLookup:
        break;
      case ApiModel.ReportTemplateSentilinkScores:
        await SessionClientDashboard.postResourceCreate<Report>({
          type: ApiModel.ReportSentilinkScores,
          attributes: {
            query: {
              'user-id': account.id,
              'environment-id': environment.id,
              'report-template-id': reportTemplate.id,
              'name-first': nameFirst,
              'name-last': nameLast,
              birthdate: birthdate,
              'social-security-number': ssnField?.value?.identification_number?.value,
              'address-street-1': addressStreet1,
              'address-street-2': addressStreet2,
              'address-city': addressCity,
              'address-subdivision': addressSubdivision,
              'address-postal-code': addressPostalCode,
              'country-code': addressCountryCode,
              'phone-number': phoneNumber,
              'email-address': emailAddress,
            },
          } as Partial<ReportSentilinkScoresAttributes>,
        });
        break;
      case ApiModel.ReportTemplateSocialMedia:
        await SessionClientDashboard.postResourceCreate<Report>({
          type: ApiModel.ReportSocialMedia,
          attributes: {
            'account-id': account.id,
            'environment-id': environment.id,
            'report-template-id': reportTemplate.id,
            'phone-number': phoneNumber,
            'email-address': emailAddress,
            'name-first': nameFirst,
            'name-middle': nameMiddle,
            'name-last': nameLast,
            'address-city': addressCity,
            'address-subdivision': addressSubdivision,
            'address-postal-code': addressPostalCode,
          } as ReportSocialMediaAttributes,
        });
        break;
      case ApiModel.ReportTemplateSynthetic:
        await SessionClientDashboard.postResourceCreate<Report>({
          type: ApiModel.ReportSynthetic,
          attributes: {
            'account-id': account.id,
            'environment-id': environment.id,
            'report-template-id': reportTemplate.id,
            'name-first': nameFirst,
            'name-last': nameLast,
            birthdate: birthdate,
            'social-security-number': ssnField?.value?.identification_number?.value,
            'address-street-1': addressStreet1,
            'address-street-2': addressStreet2,
            'address-city': addressCity,
            'address-subdivision': addressSubdivision,
            'address-postal-code': addressPostalCode,
            'country-code': addressCountryCode,
            'phone-number': phoneNumber,
            'email-address': emailAddress,
          } as ReportSyntheticAttributes,
        });
        break;
      case ApiModel.ReportTemplateWatchlist:
        await SessionClientDashboard.postResourceCreate<Report>({
          type: ApiModel.ReportWatchlist,
          attributes: {
            'account-id': account.id,
            'environment-id': environment.id,
            'report-template-id': reportTemplate.id,
            'name-first': nameFirst,
            'name-middle': nameMiddle,
            'name-last': nameLast,
            birthdate: birthdate,
            'country-code': addressCountryCode,
          } as ReportWatchlistAttributes,
        });
        break;
      // Creating the following report types is
      // currently not supported through dashboard
      case ApiModel.ReportTemplateBusinessAdverseMedia:
      case ApiModel.ReportTemplateBusinessAssociatedPersons:
      case ApiModel.ReportTemplateBusinessClassification:
      case ApiModel.ReportTemplateBusinessIndustryClassification:
      case ApiModel.ReportTemplateBusinessLiensLookup:
      case ApiModel.ReportTemplateBusinessLookup:
      case ApiModel.ReportTemplateBusinessNonprofit:
      case ApiModel.ReportTemplateBusinessOnlinePresence:
      case ApiModel.ReportTemplateBusinessRegistrationsLookup:
      case ApiModel.ReportTemplateBusinessSocialMedia:
      case ApiModel.ReportTemplateBusinessWatchlist:
      case ApiModel.ReportTemplateBusinessWebsite:
      case ApiModel.ReportTemplateChainalysisAddressScreening:
      case ApiModel.ReportTemplateClearbitBusinessLookup:
      case ApiModel.ReportTemplateCoinbaseCheckCryptoRisk:
      case ApiModel.ReportTemplateCryptoAddressWatchlist:
      case ApiModel.ReportTemplateCustomList:
      case ApiModel.ReportTemplateEquifaxOneview:
      case ApiModel.ReportTemplateFinraBrokerCheck:
      case ApiModel.ReportTemplateKyckrBusinessLookup:
      case ApiModel.ReportTemplateMiddeskBusinessLookup:
      case ApiModel.ReportTemplateMxAccount:
      case ApiModel.ReportTemplateScreening:
      case ApiModel.ReportTemplateSentilinkApplicationRisk:
      case ApiModel.ReportTemplateTrmWalletScreening:
        break;

      default:
        assertNever(reportTemplate);
    }
  };
};

export const runReportAdhoc = (id: string) => {
  return async () => {
    await SessionClientDashboard.postResourceAction<Report>(
      {
        id: id,
        type: ApiModel.Report,
      },
      'run',
    );
  };
};

export const pauseReport = (
  id: string,
  accountReports?: Report[],
  inquiryReports?: Report[],
  caseObjects?: CaseObject[],
): DashboardThunkAction => {
  return async (dispatch: Dispatch, getState) => {
    const { data } = await SessionClientDashboard.postResourceAction<Report>(
      {
        id: id,
        type: ApiModel.Report,
      },
      'pause',
    );

    // Set report for Reports
    dispatch(setReport(data));

    // Set report for Accounts
    if (accountReports) {
      dispatch(setAccountReports(accountReports.map((r) => (r.id === data.id ? data : r))));
    }
    // Set report for Inquiries
    if (inquiryReports) {
      dispatch(setInquiryReports(inquiryReports.map((r) => (r.id === data.id ? data : r))));
    }
    // Set report for Cases
    const caseToken = getState().case.caseToken;
    if (caseObjects && caseToken) {
      dispatch(
        setCaseObjects({
          key: caseToken,
          objects: caseObjects.map((o) => (o.id === data.id ? data : o)),
        }),
      );
    }
  };
};

export const resumeReport = (
  id: string,
  accountReports?: Report[],
  inquiryReports?: Report[],
  caseObjects?: CaseObject[],
): DashboardThunkAction => {
  return async (dispatch: Dispatch, getState) => {
    const { data } = await SessionClientDashboard.postResourceAction<Report>(
      {
        id: id,
        type: ApiModel.Report,
      },
      'resume',
    );

    // Set report for Reports
    dispatch(setReport(data));

    // Set report for Accounts
    if (accountReports) {
      dispatch(setAccountReports(accountReports.map((r) => (r.id === data.id ? data : r))));
    }
    // Set report for Inquiries
    if (inquiryReports) {
      dispatch(setInquiryReports(inquiryReports.map((r) => (r.id === data.id ? data : r))));
    }
    // Set report for Cases
    const caseToken = getState().case.caseToken;
    if (caseObjects && caseToken) {
      dispatch(
        setCaseObjects({
          key: caseToken,
          objects: caseObjects.map((o) => (o.id === data.id ? data : o)),
        }),
      );
    }
  };
};

export const redactReport = (
  id: string,
  accountReports?: Report[],
  inquiryReports?: Report[],
  caseObjects?: CaseObject[],
): DashboardThunkAction => {
  return async (dispatch: Dispatch, getState) => {
    const { data } = await SessionClientDashboard.deleteResource<Report>(ApiEndpoint.Report, id);

    // Set report for Reports
    dispatch(setReport(data));

    // Set report for Accounts
    if (accountReports) {
      dispatch(setAccountReports(accountReports.map((r) => (r.id === data.id ? data : r))));
    }
    // Set report for Inquiries
    if (inquiryReports) {
      dispatch(setInquiryReports(inquiryReports.map((r) => (r.id === data.id ? data : r))));
    }
    // Set report for Cases
    const caseToken = getState().case.caseToken;
    if (caseObjects && caseToken) {
      dispatch(
        setCaseObjects({
          key: caseToken,
          objects: caseObjects.map((o) => (o.id === data.id ? data : o)),
        }),
      );
    }
  };
};
